import * as THREE from "three";
import RAPIER from '@dimforge/rapier3d-compat';

export type PhysicsSphere = {
  mesh: THREE.Mesh;
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
}

type Vector3Like = THREE.Vector3 | { x: number; y: number; z: number };

type LaunchOptions = {
  origin?: Vector3Like;
  direction?: Vector3Like;
  speed?: number;
  spread?: number;
}

export class Sphere {
  private spheres: PhysicsSphere[] = [];
  private world: RAPIER.World | null = null;
  private scene: THREE.Scene;
  private size: number = 0.15; // 球体のサイズ
  private minLaunchSpeed = 2.0;
  private maxLaunchSpeed = 8.0;
  private launchSpread = THREE.MathUtils.degToRad(60);
  private launchOrigin: THREE.Vector3 = new THREE.Vector3(3, 2, 0);; // 発射原点
  private launchDirection: THREE.Vector3 = new THREE.Vector3(-3, 1, 0); // 発射方向

  // Sphere
  private material: THREE.MeshLambertMaterial;
  private geometry: THREE.SphereGeometry;

  // Sound
  private firingSound: THREE.Audio;

  constructor(scene: THREE.Scene, assetsBase: string, world?: RAPIER.World) {
    this.scene = scene;
    this.world = world || null;

    // Three.js Object
    this.geometry = new THREE.SphereGeometry(this.size, 16, 16);
    this.material = new THREE.MeshLambertMaterial({
      color: 0xff6b6b,
      opacity: 1.0
    });

    const listener = new THREE.AudioListener();
    scene.add(listener);
    this.firingSound = new THREE.Audio(listener);
    const audioLoader = new THREE.AudioLoader();
    audioLoader.load(assetsBase + '/sounds/firing-sound.mp3', (buffer) => {
      this.firingSound.setBuffer(buffer);
      this.firingSound.setLoop(false);
      this.firingSound.setVolume(0.5);
    });

  }

  /**
  * 球体のインスタンスを取得
  */
  public getSpheres = (): PhysicsSphere[] => this.spheres;

  /**
   * 物理演算ワールドを設定
   */
  public setWorld = (world: RAPIER.World): void => {
    this.world = world;
  }

  /**
   * 球体を発射し、後は重力に任せる
   */
  public createFallingSphere = (): void => {
    if (!this.world) return;

    // 音声が既に再生中の場合は停止してから再生
    if (this.firingSound.isPlaying) {
      this.firingSound.stop();
    }
    this.firingSound.play();

    // よく跳ねるボールにするか
    const random = Math.random();
    let geometry = this.geometry;
    let material = this.material;
    let size = this.size;
    const colliderOption = {
      restitution: 1.0, // 反発係数
      friction: 0.7, // 摩擦係数
      density: 5.0 // 密度
    }
    const bodyOption = {
      gravityScale: 1.0, // 重力の影響
      linearDamping: 0.1, // 空気抵抗
      angularDamping: 0.5 // 回転抵抗
    }
    if (random < 0.2) {
      material = new THREE.MeshLambertMaterial({
        color: 0xff8c00,
        opacity: 1.0
      });
      size = this.size * 2;
      geometry = new THREE.SphereGeometry(size, 16, 16);
      colliderOption.restitution = 1.0;
      colliderOption.friction = 0.3;
      colliderOption.density = 0.1;
      bodyOption.gravityScale = 0.8;
      bodyOption.linearDamping = 0.3;
    } else if (random > 0.8) {
      material = new THREE.MeshLambertMaterial({
        color: 0x33aaff,
        opacity: 1.0
      });
      colliderOption.restitution = 10.0;
      colliderOption.friction = 10.0;
      colliderOption.density = 100.0;
      bodyOption.gravityScale = 20.0;
      bodyOption.linearDamping = 10.3;
    }
    // 3Dメッシュを作成
    const mesh = new THREE.Mesh(geometry, material);


    const { origin, direction, speed } = this.resolveLaunchParameters();

    mesh.position.copy(origin);
    this.scene.add(mesh);

    // 物理ボディを作成
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(origin.x, origin.y, origin.z)
      .setGravityScale(1.0)
      .setLinearDamping(0.1)
      .setAngularDamping(0.5);

    const body = this.world.createRigidBody(bodyDesc);
    body.setLinvel({
      x: direction.x * speed,
      y: direction.y * speed,
      z: direction.z * speed
    }, true);

    // コライダーを作成（Rapier3Dでは球体）
    const colliderDesc = RAPIER.ColliderDesc.ball(size)
      .setRestitution(0.6)
      .setFriction(0.7)
      .setDensity(3.0);

    const collider = this.world.createCollider(colliderDesc, body);

    // オブジェクトを保存
    const sphere: PhysicsSphere = {
      mesh,
      body,
      collider
    };

    this.spheres.push(sphere);
  }

  /**
   * 物理演算の位置を3Dメッシュに反映
   */
  public update = (): void => {
    // 配列を逆順でループして削除処理を行う
    for (let i = this.spheres.length - 1; i >= 0; i--) {
      const sphere = this.spheres[i];
      const position = sphere.body.translation();

      // Y座標が-5以下の場合は削除
      if (position.y < -5) {
        this.removeSphere(i);
        continue;
      }

      const rotation = sphere.body.rotation();

      // 位置を更新（Rapier3Dの座標をThree.jsの3D座標に変換）
      sphere.mesh.position.set(position.x, position.y, position.z);

      // 回転を更新（クォータニオンを直接使用）
      const quaternion = new THREE.Quaternion(
        rotation.x, rotation.y, rotation.z, rotation.w
      );
      sphere.mesh.quaternion.copy(quaternion);
    }
  }



  /**
   * 全ての球体を削除
   */
  public clear = (): void => {
    this.spheres.forEach(sphere => {
      this.scene.remove(sphere.mesh);

      // Three.jsのリソースを明示的に破棄
      if (sphere.mesh.geometry) {
        sphere.mesh.geometry.dispose();
      }
      if (sphere.mesh.material) {
        if (Array.isArray(sphere.mesh.material)) {
          sphere.mesh.material.forEach(material => material.dispose());
        } else {
          sphere.mesh.material.dispose();
        }
      }

      if (this.world) {
        this.world.removeRigidBody(sphere.body);
      }
    });
    this.spheres = [];
  }

  /**
   * 指定されたインデックスの球体を削除
   */
  private removeSphere = (index: number): void => {
    if (index < 0 || index >= this.spheres.length) return;

    const sphere = this.spheres[index];

    // シーンからメッシュを削除
    this.scene.remove(sphere.mesh);

    // Three.jsのリソースを明示的に破棄
    if (sphere.mesh.geometry) {
      sphere.mesh.geometry.dispose();
    }
    if (sphere.mesh.material) {
      if (Array.isArray(sphere.mesh.material)) {
        sphere.mesh.material.forEach(material => material.dispose());
      } else {
        sphere.mesh.material.dispose();
      }
    }

    // 物理ワールドからボディを削除
    if (this.world) {
      this.world.removeRigidBody(sphere.body);
    }

    // 配列から削除
    this.spheres.splice(index, 1);
  }

  // ------------------------ Private ------------------------
  /**
   * 発射パラメータ
   * @param options
   * @returns
   */
  private resolveLaunchParameters = () => {
    const origin = this.cloneVector(this.launchOrigin);
    const baseDirection = this.cloneVector(this.launchDirection);
    const spread = this.launchSpread;

    if (spread > 0) {
      const yaw = (Math.random() - 0.5) * spread;
      const pitch = (Math.random() - 0.5) * spread * 0.5;
      const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, yaw, 0, 'XYZ'));
      baseDirection.applyQuaternion(rotation);
    }

    if (baseDirection.lengthSq() === 0) {
      baseDirection.copy(this.launchDirection);
    }
    baseDirection.normalize();

    const speed = this.randomBetween(this.minLaunchSpeed, this.maxLaunchSpeed);

    return {
      origin,
      direction: baseDirection,
      speed
    };
  }

  private cloneVector = (value: Vector3Like): THREE.Vector3 => {
    if (value instanceof THREE.Vector3) {
      return value.clone();
    }
    return new THREE.Vector3(value.x, value.y, value.z);
  }

  private randomBetween = (min: number, max: number): number => min + Math.random() * (max - min);
}
